import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, User, LogOut, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useAccount } from '../context/AccountContext';
import { useTheme } from '../context/ThemeContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import AuthModal from './auth/AuthModal';
import NavigationDrawer from './nav/NavigationDrawer';
import CountryCurrencyPicker from './nav/CountryCurrencyPicker';
import { PRODUCT_TABS } from './search/ProductTabsBar';
import { useModulesConfig } from '../lib/modules';

// Modern animated dark/light toggle pill.
const ThemeSwitch = ({ value, onToggle, transparent = false }) => (
  <button
    type="button"
    onClick={onToggle}
    role="switch"
    aria-checked={value === 'dark'}
    data-testid="theme-toggle"
    title="Toggle theme"
    className={`relative inline-flex items-center w-14 h-9 rounded-full transition-colors duration-300 ${
      transparent
        ? 'bg-white/15 backdrop-blur-md border border-white/20'
        : (value === 'dark' ? 'bg-gray-700' : 'bg-amber-200/80')
    }`}
  >
    <span className="sr-only">Toggle dark mode</span>
    <span
      className={`absolute top-1 grid place-items-center h-7 w-7 rounded-full shadow-md bg-white transition-transform duration-300 ${value === 'dark' ? 'translate-x-6' : 'translate-x-1'}`}
    >
      {value === 'dark'
        ? <Moon className="h-3.5 w-3.5 text-gray-700" />
        : <Sun className="h-3.5 w-3.5 text-amber-500" />}
    </span>
  </button>
);

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('personal');
  const [scrolled, setScrolled] = useState(false);
  const { modules: modulesEnabled, order: moduleOrder } = useModulesConfig();
  const { accountType, toggleAccountType } = useAccount();
  const { theme, toggleTheme } = useTheme();
  const { customer, logout } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Routes that should NOT show the Personal/Business toggle.
  // Currently: only homepage shows it. Search results, booking, admin all hide it.
  const isHome = location.pathname === '/';
  // Results pages (/<module>/search) — show modules in navbar center always, hide navbar on scroll past 80px.
  const isResultsPage = /^\/(hotels|flights|trains|buses|cabs|villas|packages|tours|visa|cruise|forex|insurance)\/search/.test(location.pathname);
  const transparent = isHome && !scrolled;
  // MMT-style: navbar shows modules in CENTER on (a) home-after-scroll OR (b) results pages always.
  const showCenterModules = (isHome && scrolled) || isResultsPage;

  // Home navbar transitions from transparent → solid on scroll. Results page navbar
  // is in normal document flow (`relative`) so it scrolls away naturally; no scroll
  // listener is needed there.
  useEffect(() => {
    if (!isHome) { setScrolled(true); return; }
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  // Load modules CMS so the navbar center matches the homepage Functions card.
  // Respects admin-defined order.
  const byKey = Object.fromEntries(PRODUCT_TABS.map((t) => [t.value, t]));
  const orderedKeys = Array.isArray(moduleOrder) && moduleOrder.length ? moduleOrder : PRODUCT_TABS.map((t) => t.value);
  const visibleTabs = orderedKeys
    .map((k) => byKey[k])
    .filter((t) => t && modulesEnabled[t.value] === true);

  const openAuth = (tab = 'personal') => { setAuthTab(tab); setAuthOpen(true); };

  // Colour palette + position:
  // - Home pre-scroll: fixed transparent (over hero image)
  // - Home post-scroll / other pages: sticky top-0 (always visible)
  // - Results page: relative (scrolls AWAY with page so the search bar pops to top — MMT behavior)
  const navCls = transparent
    ? 'fixed top-0 inset-x-0 z-50 bg-transparent border-b border-transparent text-white'
    : isResultsPage
      ? 'relative z-50 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white'
      : 'sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white';

  const iconBtnCls = transparent
    ? 'p-2 rounded-lg hover:bg-white/15 transition-colors'
    : 'p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors';

  return (
    <>
      <nav className={`${navCls} transition-all duration-300`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
            {/* LEFT — hamburger + logo */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={() => setDrawerOpen(true)}
                data-testid="nav-hamburger"
                aria-label="Open menu"
                className={`tap-target ${iconBtnCls}`}
              >
                <Menu className={`h-6 w-6 ${transparent ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`} />
              </button>
              <Link to="/" className="flex items-center gap-2 group min-w-0" data-testid="nav-logo">
                <img
                  src="/elite-travo-logo.png"
                  alt="Elite Travo"
                  className="h-9 w-9 sm:h-10 sm:w-10 object-contain transform group-hover:scale-105 transition-transform duration-200"
                />
                <span className={`text-base sm:text-xl font-bold truncate ${transparent ? 'text-white drop-shadow' : ''}`}>
                  Elite<span className={accountType === 'personal' ? 'text-red-500' : 'text-red-400'}>Travo</span>
                </span>
              </Link>
            </div>

            {/* CENTER — service modules. Highlights active module from URL. Clicks navigate to
                homepage with that module pre-selected (matches MMT behavior). */}
            {showCenterModules && (
              <div className="hidden md:flex flex-1 items-center justify-center min-w-0 px-2" data-testid="nav-center-modules">
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                  {visibleTabs.map((t) => {
                    const activeModule = (location.pathname.match(/^\/([^/]+)\/search/) || [])[1];
                    const isActive = activeModule === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => navigate(`/?tab=${t.value}`)}
                        data-testid={`nav-module-${t.value}`}
                        className={`group shrink-0 flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-all relative
                          ${isActive
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                      >
                        <t.Icon className="h-6 w-6" />
                        <span className="text-xs font-semibold leading-none">{t.label}</span>
                        {isActive && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-full bg-red-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* RIGHT — controls */}
            <div className="flex items-center gap-1 sm:gap-3 shrink-0">
              {/* Section F: hide currency / theme / PB toggle when scrolled on home */}
              {!showCenterModules && <CountryCurrencyPicker compact={transparent} />}

              {!showCenterModules && (
                <div className="hidden md:flex">
                  <ThemeSwitch value={theme} onToggle={toggleTheme} transparent={transparent} />
                </div>
              )}

              {/* Personal/Business toggle — visible only on homepage AND when not scrolled */}
              {isHome && !showCenterModules && (
                <div className={`hidden md:flex items-center rounded-full p-1 h-9 ${transparent ? 'bg-white/15 backdrop-blur-md border border-white/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
                  <button
                    onClick={() => accountType === 'business' && toggleAccountType()}
                    data-testid="nav-account-personal"
                    className={`px-3 h-7 rounded-full text-xs font-semibold transition-all duration-200 ${
                      accountType === 'personal'
                        ? 'bg-white text-red-600 shadow-sm'
                        : (transparent ? 'text-white/90 hover:text-white' : 'text-gray-600 dark:text-gray-400')
                    }`}
                  >Personal</button>
                  <button
                    onClick={() => accountType === 'personal' && toggleAccountType()}
                    data-testid="nav-account-business"
                    className={`px-3 h-7 rounded-full text-xs font-semibold transition-all duration-200 ${
                      accountType === 'business'
                        ? 'bg-white text-red-700 shadow-sm'
                        : (transparent ? 'text-white/90 hover:text-white' : 'text-gray-600 dark:text-gray-400')
                    }`}
                  >Business</button>
                </div>
              )}

              <div className="hidden md:flex items-center">
                {customer ? (
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${transparent ? 'bg-white/15 backdrop-blur-md border border-white/20' : 'bg-gray-100 dark:bg-gray-800'}`} data-testid="customer-user-chip">
                      {customer.avatar ? (
                        <img src={customer.avatar} alt={customer.name} className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-red-500 to-red-700 grid place-items-center text-[11px] font-bold text-white">
                          {(customer.name || customer.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <span className={`text-sm font-semibold max-w-[120px] truncate ${transparent ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>{customer.name || customer.email}</span>
                      {customer.account_type === 'corporate' && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-300 text-amber-900">biz</span>
                      )}
                    </div>
                    <button onClick={logout} title="Logout" data-testid="customer-logout"
                            className={`tap-target ${iconBtnCls}`}>
                      <LogOut className={`h-4 w-4 ${transparent ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                    </button>
                  </div>
                ) : (
                  <Button onClick={() => openAuth(accountType === 'business' ? 'business' : 'personal')}
                          variant="ghost" data-testid="nav-login-desktop"
                          className={`font-medium text-sm ${transparent ? 'text-white hover:bg-white/15' : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-500'}`}>
                    <User className="h-4 w-4 mr-2" /> Login or Sign up
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <NavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenAuth={() => openAuth(accountType === 'business' ? 'business' : 'personal')}
      />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} />
    </>
  );
};

export default Navbar;
